import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { useLogger } from '@/lib/logger';
import { addBusinessContext } from '@/lib/sentry';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { parseSupplyRefusal } from '@/lib/trpc-error-utils';
import { Product } from '../types';
import { isOwnRow, supplyAliasRefusalText } from '../utils/supplyAliasCopy';
import { justInTimeStockRefusal } from '../utils/justInTimeCopy';
import { inventoryApi, ProcessResult } from '../api/inventoryApi';
import type {
  AddStockInput,
  SetStockInput,
  DeductStockInput,
  CreateSkuInput,
  UpdateProductInput,
  BulkAddStockInput,
} from '@threadheads/shared';

/**
 * Hook for inventory write operations (mutations)
 * Handles all create, update, and delete operations with error handling and toasts
 */
export function useInventoryMutations(products: Product[] = []) {
  const { t } = useTranslation('inventory');
  // `allLocations`, not `locations`: every use below RESOLVES A NAME for a log
  // line or a toast about a write that has already been addressed to a
  // location id. Stock writes are permitted at delegated locations
  // (~200 live sku_inventory rows sit at one), so the filtered list would
  // label those operations "Unknown" (BLU-879 review).
  const { selectedLocation, allLocations: locations } = useLocation();
  const { organization } = useOrganization();
  const { can } = useRole();
  // Cost fields need skus.costs_manage on the server; sending one without it is a 403.
  const canWriteCosts = can('skus.costs_manage');
  const utils = trpc.useUtils();
  const logger = useLogger();

  /**
   * Phase 92 (D-13, UI-SPEC Screen 4 R3) / BLU-1400: the sentence for a
   * supply alias refusal (`supplyAliasRefusalText`), or null for any other
   * error. Never the raw message. This only finds the product row it names:
   * by id first, then by the SKU code in the refusal (alias rows preferred,
   * since an alias carries its blank's code).
   */
  // An expected refusal in plain words, or null: a just-in-time pair (BLU-1268,
  // no stock number to change), or a supply alias (Phase 92).
  const aliasRefusalText = (err: unknown, skuId?: string, skuCode?: string): string | null => {
    const justInTime = justInTimeStockRefusal(t, err);
    if (justInTime) return justInTime;
    const code = parseSupplyRefusal(err)?.sku ?? skuCode;
    const row =
      (skuId ? products.find((p) => p.id === skuId) : undefined) ??
      (code ? products.find((p) => p.sku === code && p.supplyAlias) : undefined);
    return supplyAliasRefusalText(t, err, { row, activeOrganizationId: organization?.id });
  };

  // ========================================================================
  // ADD STOCK
  // ========================================================================
  const addStockMutation = trpc.inventory.addStock.useMutation({
    onMutate: async (input: AddStockInput) => {
      if (!organization) throw new Error("No organization selected");

      const product = products.find(p => p.id === input.skuId);
      const sku = product?.sku || 'Unknown';
      const location = locations.find((loc) => loc.id === input.locationId) ?? null;
      const locationName = location?.name ?? selectedLocation?.name ?? 'Unknown';

      addBusinessContext({
        operation: 'add_stock',
        sku,
        organizationId: organization.id,
        locationId: input.locationId
      });

      logger.info('Adding stock to inventory', {
        sku,
        quantity: input.quantity,
        organizationId: organization.id,
        locationId: input.locationId,
        locationName,
        operation: 'add_stock'
      });

      return { sku, locationId: input.locationId, locationName };
    },
    onSuccess: (newStock, variables: AddStockInput, context) => {
      void utils.inventory.getProducts.invalidate();
      void utils.inventory.getProducts.invalidate({ locationId: variables.locationId });

      const locationName =
        context?.locationName ??
        locations.find((loc) => loc.id === variables.locationId)?.name ??
        selectedLocation?.name ??
        'Unknown';

      logger.stockOperation('add', true, {
        sku: context?.sku || 'Unknown',
        quantity: variables.quantity,
        locationId: variables.locationId,
        locationName,
        newStock: newStock.data
      });

      // BLU-3xx Phase 47.01.2-B-fix3.3: drop the legacy "Stock Added" toast.
      // The 30-second sonner Undo toast (`useReceiveStockUndo` in
      // AddStockModal) is the single feedback signal. Two stacked toasts
      // on the same successful action read as buggy, and the legacy toast
      // had no dismiss affordance.
    },
    onError: (err, variables: AddStockInput, context) => {
      const error = err instanceof Error ? err : new Error('Unknown error');
      const locationName =
        context?.locationName ??
        locations.find((loc) => loc.id === variables.locationId)?.name ??
        selectedLocation?.name ??
        'Unknown';

      logger.stockOperation('add', false, {
        sku: context?.sku || 'Unknown',
        quantity: variables.quantity,
        locationId: variables.locationId,
        locationName,
        error: error.message
      });

      const refusal = aliasRefusalText(err, variables.skuId);
      if (refusal) {
        // An expected refusal, not a fault: no Sentry event.
        toast({ title: t('mutations.failedAddStock'), description: refusal, variant: 'destructive' });
        return;
      }

      Sentry.captureException(err, {
        tags: {
          component: 'useInventory',
          operation: 'addStock',
          location_id: variables.locationId,
          location_name: locationName
        },
        extra: {
          sku: context?.sku,
          quantity: variables.quantity,
          error: error.message
        }
      });

      toast({
        title: t('mutations.failedAddStock'),
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // ========================================================================
  // SET STOCK
  // ========================================================================
  const setStockMutation = trpc.inventory.setStock.useMutation({
    onMutate: async (input: SetStockInput) => {
      if (!organization) throw new Error("No organization selected");

      const product = products.find(p => p.id === input.skuId);
      const sku = product?.sku || 'Unknown';
      const location = locations.find((loc) => loc.id === input.locationId) ?? null;
      const locationName = location?.name ?? selectedLocation?.name ?? 'Unknown';

      addBusinessContext({
        operation: 'set_stock',
        sku,
        organizationId: organization.id,
        locationId: input.locationId,
        newStock: input.quantity
      });

      return {
        sku,
        source: input.reason?.includes('csv') ? 'csv_import' : 'manual',
        locationId: input.locationId,
        locationName,
      };
    },
    onSuccess: (_data, variables: SetStockInput, context) => {
      void utils.inventory.getProducts.invalidate();
      void utils.inventory.getProducts.invalidate({ locationId: variables.locationId });

      const locationName =
        context?.locationName ??
        locations.find((loc) => loc.id === variables.locationId)?.name ??
        selectedLocation?.name ??
        'Unknown';

      logger.stockOperation('set', true, {
        sku: context?.sku || 'Unknown',
        quantity: variables.quantity,
        locationId: variables.locationId,
        locationName,
        newStock: variables.quantity,
        source: context?.source
      });

      // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 1): drop the legacy "Stock
      // Level Updated" toast — context.sku resolution via products.find
      // returns "Unknown" for cross-org SKUs (page-level products array
      // is own-org only). Aligns with B-fix3.3's addStock cleanup; the
      // form reset + matrix refresh is sufficient implicit confirmation.
    },
    onError: (err, variables: SetStockInput, context) => {
      const error = err instanceof Error ? err : new Error('Unknown error');
      const locationName =
        context?.locationName ??
        locations.find((loc) => loc.id === variables.locationId)?.name ??
        selectedLocation?.name ??
        'Unknown';

      logger.stockOperation('set', false, {
        sku: context?.sku || 'Unknown',
        quantity: variables.quantity,
        locationId: variables.locationId,
        locationName,
        error: error.message,
        source: context?.source
      });

      toast({
        title: t('mutations.failedUpdateStock'),
        description: aliasRefusalText(err, variables.skuId) ?? error.message,
        variant: "destructive",
      });
    }
  });

  const moveToUnavailableMutation = trpc.inventory.moveToUnavailable.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }
      // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 1): drop legacy toast — same
      // cleanup as setStock/addStock. The matrix refresh + modal close
      // is sufficient feedback.
    },
    onError: (error, variables) => {
      toast({
        title: t('mutations.failedToMove'),
        description: aliasRefusalText(error, variables.skuId) ?? error.message,
        variant: 'destructive',
      });
    },
  });

  const moveToAvailableMutation = trpc.inventory.moveToAvailable.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }
      // BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 1): drop legacy toast — see
      // setStock/moveToUnavailable for full rationale.
    },
    onError: (error, variables) => {
      toast({
        title: t('mutations.failedToRelease'),
        description: aliasRefusalText(error, variables.skuId) ?? error.message,
        variant: 'destructive',
      });
    },
  });

  // ========================================================================
  // ADD SKU
  // ========================================================================
  const addSkuMutation = trpc.inventory.createSku.useMutation({
    onSuccess: (data, variables: CreateSkuInput) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      const message = data.isReused
        ? `${variables.sku} has been added to ${selectedLocation?.name ?? 'Unknown'} (existing SKU reused).`
        : `${variables.sku} has been successfully added to ${selectedLocation?.name ?? 'Unknown'}.`;

      toast({
        title: t('mutations.skuAdded'),
        description: message,
      });
    },
    onError: (err, variables: CreateSkuInput) => {
      // Phase 92: creating a SKU whose code is an existing supply alias is
      // refused before any write.
      const refusal = aliasRefusalText(err, undefined, variables.sku);
      if (refusal) {
        toast({ title: t('mutations.errorAddingSku'), description: refusal, variant: 'destructive' });
        return;
      }

      Sentry.captureException(err, {
        tags: {
          component: 'useInventory',
          operation: 'addSku',
          location_id: selectedLocation?.id,
          location_name: selectedLocation?.name
        },
        extra: {
          newSku: variables.sku,
          category: variables.category,
          stockLevel: variables.stockLevel,
          error: err instanceof Error ? err.message : 'Unknown error'
        }
      });

      toast({
        title: t('mutations.errorAddingSku'),
        description: err instanceof Error ? err.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  });

  // ========================================================================
  // DEDUCT STOCK
  // ========================================================================
  const deductStockMutation = trpc.inventory.deductStock.useMutation({
    onMutate: async (input: DeductStockInput) => {
      if (!selectedLocation || !organization) throw new Error("No location or organization selected");

      const product = products.find(p => p.id === input.skuId);
      const sku = product?.sku || 'Unknown';

      const resolvedSource = input.source || 'manual';

      addBusinessContext({
        operation: 'deduct_stock',
        sku,
        organizationId: organization.id,
        locationId: selectedLocation.id,
        quantity: input.quantity,
        source: resolvedSource,
      });

      logger.info('Deducting stock from inventory', {
        operation: 'deduct_stock',
        sku,
        quantity: input.quantity,
        organizationId: organization.id,
        locationId: selectedLocation.id,
        locationName: selectedLocation.name,
        source: resolvedSource,
        referenceType: input.referenceType,
        referenceId: input.referenceId,
      });

      return { sku, source: resolvedSource };
    },
    onSuccess: (_data, variables: DeductStockInput, context) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      logger.stockOperation('remove', true, {
        sku: context?.sku || 'Unknown',
        quantity: variables.quantity,
        locationId: selectedLocation?.id ?? 'unknown',
        locationName: selectedLocation?.name ?? 'Unknown',
        source: context?.source,
        referenceType: variables.referenceType,
        referenceId: variables.referenceId,
      });

      if (!variables.source || variables.source === 'manual') {
        toast({
          title: t('mutations.stockDeducted'),
          description: t('mutations.stockDeductedDesc', { quantity: variables.quantity, sku: context?.sku || 'product' }),
        });
      }
    },
    onError: (err, variables: DeductStockInput, context) => {
      const error = err instanceof Error ? err : new Error('Unknown error');

      logger.stockOperation('remove', false, {
        sku: context?.sku || 'Unknown',
        quantity: variables.quantity,
        locationId: selectedLocation?.id ?? 'unknown',
        locationName: selectedLocation?.name ?? 'Unknown',
        source: context?.source,
        referenceType: variables.referenceType,
        referenceId: variables.referenceId,
        error: error.message,
      });

      if (!variables.source || variables.source === 'manual') {
        toast({
          title: t('mutations.failedDeductStock'),
          description: aliasRefusalText(err, variables.skuId) ?? error.message,
          variant: "destructive",
        });
      }
    },
  });

  // ========================================================================
  // UPDATE PRODUCT
  // ========================================================================
  const updateProductMutationTrpc = trpc.inventory.updateProduct.useMutation({
    onSuccess: (_data, variables: UpdateProductInput) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      toast({
        title: t('mutations.productUpdated'),
        description: `${variables.sku} has been successfully updated at ${selectedLocation?.name ?? 'Unknown'}.`,
      });
    },
    onError: (err, variables: UpdateProductInput) => {
      Sentry.captureException(err, {
        tags: {
          component: 'useInventory',
          operation: 'updateProduct',
          location_id: selectedLocation?.id,
          location_name: selectedLocation?.name
        },
        extra: {
          productId: variables.productId,
          productSku: variables.sku,
          stockLevel: variables.stockLevel,
          error: err instanceof Error ? err.message : 'Unknown error'
        }
      });

      toast({
        title: t('mutations.updateFailed'),
        description:
          aliasRefusalText(err, variables.productId, variables.sku) ??
          (err instanceof Error ? err.message : 'Unknown error occurred'),
        variant: 'destructive',
      });
    }
  });

  // Wrapper that handles file upload before calling tRPC
  const updateProductMutation = {
    mutateAsync: async ({ product, imageFile }: { product: Product; imageFile?: File }) => {
      if (!selectedLocation || !organization) throw new Error("No location or organization selected");

      let imageUrl = product.image_url;

      // Upload image if provided (client-side Supabase Storage)
      if (imageFile) {
        imageUrl = await inventoryApi.uploadProductImage(imageFile, product.sku);
      }

      // Call tRPC to update product
      return await updateProductMutationTrpc.mutateAsync({
        productId: product.id,
        locationId: selectedLocation.id,
        sku: product.sku,
        category: product.category,
        stockLevel: product.stockLevel,
        notify: product.notify,
        imageUrl: imageUrl || '',
        productionType: product.productionType,
        skuCostOverride: canWriteCosts ? product.skuCostOverride ?? null : undefined,
      });
    }
  };

  // ========================================================================
  // BULK ADD STOCK
  // ========================================================================
  const bulkAddStockMutation = trpc.inventory.bulkAddStock.useMutation({
    onMutate: async (input: BulkAddStockInput) => {
      if (!selectedLocation || !organization) throw new Error("No location or organization selected");

      // Per-SKU path (BulkStockEditorDialog): each row carries its own
      // quantity and the shared `quantity` is only a schema-level fallback.
      // Reporting that fallback ("Added 1 units to 8 products") would be a
      // lie, so the toast switches to resolved totals whenever any row
      // overrides it. The same-quantity path (BulkActionModal) is unchanged.
      const isPerSku = input.productIds.some((p) => typeof p.quantity === 'number');
      const totalUnits = input.productIds.reduce(
        (sum, p) => sum + (p.quantity ?? input.quantity),
        0,
      );

      addBusinessContext({
        operation: 'bulk_add_stock',
        organizationId: organization.id,
        locationId: selectedLocation.id,
        productCount: input.productIds.length,
        quantity: input.quantity
      });

      return {
        count: input.productIds.length,
        quantity: input.quantity,
        isPerSku,
        totalUnits,
        reference: input.reference,
        isConsume: input.direction === 'consume',
      };
    },
    onSuccess: (data, _variables, context) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      logger.info('Bulk stock operation completed', {
        operation: 'bulk_add_stock',
        locationId: selectedLocation?.id ?? 'unknown',
        locationName: selectedLocation?.name ?? 'Unknown',
        productCount: context.count,
        quantity: context.quantity,
        totalUnits: context.totalUnits,
        perSku: context.isPerSku,
        failedRows: data?.errors?.length ?? 0,
        businessImpact: 'inventory_updated'
      });

      // Partial failure is a FULFILLED response (the applied rows already
      // moved stock and are on the adjustment document) — report it
      // honestly instead of the full-success toast. `success` is the
      // server's own verdict (errors.length === 0); read it rather than
      // re-deriving from errors.
      // BLU-1268: just-in-time rows have no stock number, so the server skips
      // them and says how many; the toast says so rather than counting them.
      const skippedJustInTime = data?.skippedJustInTime ?? 0;
      const skippedNote = skippedJustInTime > 0 ? t('supply.bulkSkipped', { count: skippedJustInTime }) : null;

      if (data?.success === false) {
        toast({
          title: t('mutations.bulkStockPartial'),
          description: [
            t('mutations.bulkStockPartialDesc', {
              applied: data.results?.length ?? 0,
              failed: data.errors.length,
            }),
            skippedNote,
          ].filter(Boolean).join(' '),
          variant: 'destructive',
        });
        return;
      }

      // Consume documents get their own verbs — "Added N units" after a
      // deduction would read as the opposite of what happened.
      const perSkuKey = context.isConsume
        ? context.reference
          ? 'mutations.bulkStockConsumedPerSkuRefDesc'
          : 'mutations.bulkStockConsumedPerSkuDesc'
        : context.reference
          ? 'mutations.bulkStockAddedPerSkuRefDesc'
          : 'mutations.bulkStockAddedPerSkuDesc';
      const description = context.isPerSku
        ? t(perSkuKey, {
            units: context.totalUnits,
            count: context.count,
            reference: context.reference,
          })
        : t('mutations.bulkStockAddedDesc', { quantity: context.quantity, count: context.count });

      toast({
        title: t(context.isConsume ? 'mutations.bulkStockConsumed' : 'mutations.bulkStockAdded'),
        description: skippedNote ? `${description} ${skippedNote}` : description,
      });
    },
    onError: (err) => {
      // Partial failures now RESOLVE (handled in onSuccess); this path is
      // whole-request failures. Still refresh — a network error can land
      // after the server applied rows, and a stale sheet invites
      // re-submitting rows that already succeeded.
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      logger.error('Bulk stock operation failed', {
        operation: 'bulk_add_stock',
        locationId: selectedLocation?.id ?? 'unknown',
        error: err instanceof Error ? err.message : 'Unknown error',
        businessImpact: 'inventory_update_failed'
      });

      toast({
        title: t('mutations.bulkAddFailed'),
        description: aliasRefusalText(err) ?? (err instanceof Error ? err.message : "Unknown error occurred"),
        variant: "destructive",
      });
    }
  });

  // ========================================================================
  // BULK UPDATE IMAGE
  // ========================================================================
  const bulkUpdateImagesTrpc = trpc.inventory.bulkUpdateProductImages.useMutation();

  const bulkUpdateImageMutation = useMutation({
    mutationFn: async ({ productIds, imageFile }: { productIds: string[]; imageFile: File }) => {
      if (!selectedLocation || !organization) throw new Error("No location or organization selected");

      const publicUrl = await inventoryApi.uploadProductImage(imageFile, 'bulk');
      const { updatedCount } = await bulkUpdateImagesTrpc.mutateAsync({
        locationId: selectedLocation.id,
        productIds,
        imageUrl: publicUrl,
      });

      return updatedCount;
    },
    onSuccess: (updatedCount, variables) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      // The server writes only the active org's own SKUs and never a supply
      // alias (BLU-1400), and returns what it wrote. The active org's own
      // alias rows are always among the rows left out, so when they are the
      // whole gap the toast says so; any other gap stays generic.
      const notChanged = variables.productIds.length - updatedCount;
      let description = t('mutations.imagesUpdatedDesc', { count: updatedCount });
      if (notChanged > 0) {
        const byId = new Map(products.map((p) => [p.id, p]));
        const ownAliases = variables.productIds.filter((id) => {
          const row = byId.get(id);
          return Boolean(row?.supplyAlias) && isOwnRow(row?.ownerOrganizationId, organization?.id);
        }).length;
        description = t(
          ownAliases === notChanged ? 'mutations.imagesUpdatedAliasesLeft' : 'mutations.imagesUpdatedNotChanged',
          { updated: updatedCount, count: notChanged },
        );
      }

      toast({ title: t('mutations.imagesUpdated'), description });
    },
    onError: (err) => {
      toast({
        title: t('mutations.bulkImageUpdateFailed'),
        description: err instanceof Error ? err.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  });

  // ========================================================================
  // BULK DELETE
  // ========================================================================
  const bulkDeleteMutation = trpc.inventory.deleteProducts.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      toast({
        title: t('mutations.productsDeleted'),
        description: `Deleted products from ${selectedLocation?.name ?? 'Unknown'} only`,
      });
    },
    onError: (err) => {
      toast({
        title: t('mutations.deleteFailed'),
        // A supply alias's row is the 3PL's floor row: the refusal names the owner.
        description: aliasRefusalText(err) ?? (err instanceof Error ? err.message : "Unknown error occurred"),
        variant: "destructive",
      });
    }
  });

  // ========================================================================
  // CSV IMPORT MUTATIONS
  // ========================================================================
  const bulkAddStockFromCsvMutation = useMutation({
    mutationFn: async (csvFile: File): Promise<ProcessResult> => {
      if (!selectedLocation || !organization) throw new Error("No location or organization selected");

      return await inventoryApi.bulkAddStockFromCsv(
        csvFile,
        products,
        selectedLocation.id,
        async (sku: string, quantity: number) => {
          const product = products.find(p => p.sku === sku);
          if (!product) {
            throw new Error(`Product not found for SKU ${sku}`);
          }

          await addStockMutation.mutateAsync({
            skuId: product.id,
            locationId: selectedLocation.id,
            quantity,
            reason: 'csv_import',
          });
          return true;
        }
      );
    },
    onSuccess: () => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }
    },
    onError: (err) => {
      toast({
        title: t('mutations.csvImportFailed'),
        description: err instanceof Error ? err.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  });

  // ========================================================================
  // BULK CSV IMPORT (new wizard pipeline via tRPC)
  // ========================================================================
  const bulkCsvImportMutation = trpc.inventory.bulkCsvImport.useMutation({
    onSuccess: (data) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      if (data.success && data.failedCount === 0) {
        logger.info('Bulk CSV import completed successfully', {
          operation: 'bulk_csv_import',
          locationId: selectedLocation?.id ?? 'unknown',
          locationName: selectedLocation?.name ?? 'Unknown',
          successCount: data.successCount,
          batchId: data.batchId,
        });
      } else {
        logger.info('Bulk CSV import completed with issues', {
          operation: 'bulk_csv_import',
          locationId: selectedLocation?.id ?? 'unknown',
          successCount: data.successCount,
          failedCount: data.failedCount,
          mode: data.mode,
        });
      }
    },
    onError: (err) => {
      logger.error('Bulk CSV import failed', {
        operation: 'bulk_csv_import',
        locationId: selectedLocation?.id ?? 'unknown',
        error: err instanceof Error ? err.message : 'Unknown error',
      });

      toast({
        title: t('mutations.csvImportFailed'),
        description: err instanceof Error ? err.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  // ========================================================================
  // UNDO BULK IMPORT
  // ========================================================================
  const undoBulkImportMutation = trpc.inventory.undoBulkImport.useMutation({
    onSuccess: (data) => {
      if (selectedLocation) {
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      }

      logger.info('Bulk import undo completed', {
        operation: 'undo_bulk_import',
        locationId: selectedLocation?.id ?? 'unknown',
        undoneCount: data.undoneCount,
      });

      toast({
        title: 'Undo complete',
        description: `Reversed ${data.undoneCount} stock changes.`,
      });
    },
    onError: (err) => {
      logger.error('Bulk import undo failed', {
        operation: 'undo_bulk_import',
        locationId: selectedLocation?.id ?? 'unknown',
        error: err instanceof Error ? err.message : 'Unknown error',
      });

      toast({
        title: 'Undo failed',
        description: err instanceof Error ? err.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  return {
    addStockMutation,
    setStockMutation,
    deductStockMutation,
    addSkuMutation,
    updateProductMutation,
    bulkAddStockMutation,
    bulkUpdateImageMutation,
    bulkDeleteMutation,
    bulkAddStockFromCsvMutation,
    bulkCsvImportMutation,
    undoBulkImportMutation,
    moveToUnavailableMutation,
    moveToAvailableMutation,
  };
}
